/**
 * Hook to synchronize theme preferences between next-themes and Supabase
 */

import { useEffect, useRef } from 'react';
import { useTheme } from 'next-themes';
import { updateUserPreferences, getUserPreferences } from '@/lib/user-preferences-service';
import type { ThemePreference } from '@/lib/user-preferences';

export function useThemeSync() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const hasLoadedFromDatabase = useRef(false);
  const isInitialMount = useRef(true);

  // Load theme from database on mount (only once)
  useEffect(() => {
    if (hasLoadedFromDatabase.current) return;

    const loadThemeFromDatabase = async () => {
      try {
        const preferences = await getUserPreferences();
        
        if (preferences?.theme) {
          // Set theme from database if available
          setTheme(preferences.theme);
        } else {
          // Auto-detect system preference if no saved preference
          const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          const defaultTheme: ThemePreference = systemPrefersDark ? 'dark' : 'light';
          setTheme(defaultTheme);
          
          // Save detected theme to database
          await updateUserPreferences({ theme: defaultTheme });
        }
        hasLoadedFromDatabase.current = true;
      } catch (error) {
        console.error('[useThemeSync] Error loading theme from database:', error);
        hasLoadedFromDatabase.current = true; // Mark as loaded even on error to prevent retries
      }
    };

    loadThemeFromDatabase();
  }, [setTheme]);

  // Save theme to database when it changes (but not on initial mount)
  useEffect(() => {
    // Skip saving on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const saveThemeToDatabase = async () => {
      if (theme && theme !== 'system' && hasLoadedFromDatabase.current) {
        try {
          await updateUserPreferences({ theme: theme as ThemePreference });
        } catch (error) {
          console.error('[useThemeSync] Error saving theme to database:', error);
        }
      }
    };

    saveThemeToDatabase();
  }, [theme]);

  return { theme, setTheme, resolvedTheme };
}
